/**
 * Phase 20.6 — RGM Shuffle Radio Hero
 *
 * Pandora/Spotify-style "Start Radio" surface. User picks a station, hits
 * the big Play button, and the entire queue gets pushed into the global
 * PlayerProvider so playback continues across navigation.
 */
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { usePlayer } from "@/components/player/PlayerProvider";

const STATIONS = [
  { id: "community", label: "Community", icon: "🔀", hint: "Mixed feed" },
  { id: "artist",    label: "Artists",   icon: "🎤", hint: "Tracks only" },
  { id: "producer",  label: "Producers", icon: "🎹", hint: "Beats only" },
  { id: "trending",  label: "Trending",  icon: "🔥", hint: "Hot right now" },
  { id: "winners",   label: "Winners",   icon: "🏆", hint: "Spotlight + BOTW" },
  { id: "following", label: "Following", icon: "👥", hint: "Your follows", auth: true },
  { id: "foryou",    label: "For You",   icon: "💜", hint: "Personalised" },
];

const ShuffleRadioHero = ({ currentUser }) => {
  const player = usePlayer();
  const navigate = useNavigate();
  const [station, setStation] = useState("community");
  const [loading, setLoading] = useState(false);
  const [queueSize, setQueueSize] = useState(0);

  const startRadio = useCallback(async (sid) => {
    if (!sid) sid = station;
    const s = STATIONS.find((x) => x.id === sid);
    if (s?.auth && !currentUser) {
      toast.error("Sign in to use this station");
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/shuffle/radio?station=${sid}&limit=50`);
      const queue = res.data?.queue || [];
      if (!queue.length) {
        toast.error("No playable items in this station yet");
        return;
      }
      // Tag every item with the station so the analytics hook can ping
      // /api/shuffle/radio/event with full context.
      const queueWithMeta = queue.map((q) => ({
        ...q,
        _station: sid,
        // Map fields PlayerProvider.normaliseTrack expects:
        trackId: q.kind === "beat" ? `beat_${q.beat_id}` : q.submission_id,
        kind: q.kind, // stays in raw
      }));
      const first = queueWithMeta[0];
      player.play(first, {
        queue: queueWithMeta,
        sourceLabel: `🔀 RGM Shuffle · ${s?.label || sid}`,
      });
      setQueueSize(queueWithMeta.length);
      toast.success(`🔀 ${s?.label || sid} Radio · ${queueWithMeta.length} tracks queued`);
      // Phase 20.6.2 — Pandora-style: jump straight to the full-screen player
      navigate("/shuffle/radio-player");
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sign in for the Following station");
      } else {
        toast.error(err?.response?.data?.detail || "Could not start radio");
      }
    } finally {
      setLoading(false);
    }
  }, [station, currentUser, player, navigate]);

  const visibleStations = STATIONS.filter((s) => !s.auth || currentUser);

  return (
    <section
      className="relative overflow-hidden rounded-3xl border border-purple-400/30 bg-gradient-to-br from-purple-900/40 via-fuchsia-900/30 to-pink-900/30 p-6 sm:p-8 mb-8 shadow-[0_20px_60px_rgba(120,30,200,0.25)]"
      data-testid="shuffle-radio-hero"
    >
      {/* Decorative glow */}
      <div className="absolute -top-24 -right-24 w-72 h-72 bg-fuchsia-500/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl pointer-events-none" />

      <div className="relative">
        <p className="text-[11px] uppercase tracking-[0.3em] text-purple-200/80 font-bold">
          RGM Shuffle Radio
        </p>
        <h2 className="text-2xl sm:text-3xl font-black text-white mt-1">
          Hit Play. The whole platform sings.
        </h2>
        <p className="text-sm text-purple-200/70 mt-2 max-w-xl">
          Continuous, Pandora-style listening. Tracks <span className="text-pink-300">and</span> beats
          stitched together, smart-rotated so no creator plays back-to-back. Keep browsing —
          the music follows.
        </p>

        {/* Station chips */}
        <div
          className="flex gap-2 mt-5 overflow-x-auto pb-2 -mx-1 px-1"
          data-testid="shuffle-radio-stations"
        >
          {visibleStations.map((s) => (
            <button
              key={s.id}
              onClick={() => setStation(s.id)}
              data-testid={`shuffle-radio-station-${s.id}`}
              className={`px-3 py-1.5 rounded-full text-sm font-bold border whitespace-nowrap transition-all ${
                station === s.id
                  ? "bg-white text-purple-900 border-white scale-[1.02] shadow-lg shadow-purple-500/30"
                  : "bg-black/30 border-purple-400/30 text-purple-100 hover:border-purple-300/60"
              }`}
              title={s.hint}
            >
              <span className="mr-1">{s.icon}</span>
              {s.label}
            </button>
          ))}
        </div>

        {/* Start Radio CTA */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button
            onClick={() => startRadio(station)}
            disabled={loading}
            data-testid="shuffle-radio-start"
            className="h-12 px-6 text-base font-black bg-gradient-to-r from-pink-500 to-fuchsia-500 hover:from-pink-400 hover:to-fuchsia-400 text-white border-0 shadow-lg shadow-pink-500/30"
          >
            {loading ? "Loading…" : "▶  Start Shuffle Radio"}
          </Button>
          {queueSize > 0 && (
            <span className="text-xs text-purple-200/80" data-testid="shuffle-radio-queue-size">
              {queueSize} items in your radio queue
            </span>
          )}
        </div>
      </div>
    </section>
  );
};

export default ShuffleRadioHero;
